<!-- Energy widget version: 1.0 -->
<script lang="ts">
	import { Button, Container, Loader, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { rooms } from 'src/stores/lists/rooms';
	import { fetchEnergyStatistics } from 'src/components/EnergyMonitoring/helpers/fetch_energy_statistics';
	import {
		buildDemoRoomDistributionRows,
		buildRoomDistributionRows,
		formatRoomDistributionWh,
		type RoomDistributionRow,
	} from 'src/components/EnergyMonitoring/helpers/energy_room_distribution_helpers';
	import { getAllRoomsSorted } from 'src/components/EnergyMonitoring/helpers/energy_room_charts_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import EmWidgetState from 'src/components/EnergyMonitoring/EmWidgetState.svelte';
	import { getControlsForEnergyConfig } from '../../../../dash-energy-period';
	import { normalizeEnergyConfig } from '../../../../dash-energy-helpers';
	import type { DashEnergyWidgetConfig } from '../../../../dash-energy-types';

	export let energyConfig: DashEnergyWidgetConfig<'dashEnergyRoomDistribution'> | undefined;
	export let staticPreview = false;

	let loadToken = 0;
	let loading = true;
	let loadError = false;
	let failedRoomCount = 0;
	let rows: RoomDistributionRow[] = [];

	$: config = normalizeEnergyConfig('dashEnergyRoomDistribution', energyConfig);
	$: previewMode = staticPreview || (energyConfig as { galleryPreview?: boolean })?.galleryPreview;
	$: controls = getControlsForEnergyConfig(config);
	$: accountRooms = getAllRoomsSorted($rooms);
	$: roomsKey = accountRooms.map((room) => `${room.id}:${room.name}:${room.position ?? ''}`).join('|');
	$: visibleRows = config.showZeroConsumptionRooms
		? rows
		: rows.filter((row) => row.error || row.totalWh == null || row.totalWh > 0);
	$: maxWh = Math.max(...visibleRows.map((row) => row.totalWh ?? 0), 0);
	$: axisTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
		ratio,
		label: formatRoomDistributionWh(maxWh * ratio),
	}));
	$: hasConsumption = rows.some((row) => (row.totalWh ?? 0) > 0);

	const loadRoomDistribution = async () => {
		const token = ++loadToken;
		loadError = false;
		failedRoomCount = 0;

		if (previewMode || $energyMonitoringDataMode === 'demo') {
			rows = buildDemoRoomDistributionRows();
			loading = false;
			return;
		}

		if (!accountRooms.length) {
			rows = [];
			loading = false;
			return;
		}

		loading = true;
		const settled = await Promise.allSettled(
			accountRooms.map((room) => fetchEnergyStatistics(controls, config.period, Number(room.id))),
		);
		if (token !== loadToken) return;

		failedRoomCount = settled.filter((entry) => entry.status === 'rejected').length;
		rows = buildRoomDistributionRows(
			accountRooms.map((room, index) => ({
				room,
				result: settled[index].status === 'fulfilled' ? settled[index].value : null,
				error: settled[index].status === 'rejected',
			})),
		);
		loadError = failedRoomCount === accountRooms.length;
		loading = false;
	};

	$: void (roomsKey, controls, config.period, previewMode, loadRoomDistribution());
	const refresh = () => loadRoomDistribution();
</script>

<Container type="glass" noScroll fullHeight>
	{#if loading}
		<div class="room-distribution-loading"><Loader size="m" /></div>
	{:else if loadError}
		<EmWidgetState
			kind="error"
			message={$format('energy_monitoring_room_distribution_error', {
				default: 'Room consumption could not be loaded. Please try again.',
			})}
			minHeight="220px"
			onRefresh={refresh}
		/>
	{:else if !accountRooms.length && !previewMode && $energyMonitoringDataMode !== 'demo'}
		<EmWidgetState
			kind="setup"
			message={$format('energy_monitoring_room_distribution_setup', {
				default: 'Create rooms to compare their energy consumption.',
			})}
			minHeight="220px"
		/>
	{:else if !hasConsumption && failedRoomCount > 0}
		<EmWidgetState
			kind="error"
			message={$format('energy_monitoring_room_distribution_partial', {
				default: '{count} rooms could not be loaded.',
				values: { count: failedRoomCount },
			})}
			minHeight="220px"
			onRefresh={refresh}
		/>
	{:else if !hasConsumption}
		<EmWidgetState
			kind="empty"
			message={$format('energy_monitoring_room_distribution_empty', {
				default: 'No room consumption was recorded for the selected period.',
			})}
			minHeight="220px"
		/>
	{:else}
		<div
			class="room-distribution"
			role="img"
			aria-label={$format('energy_monitoring_room_distribution_chart_label', {
				default: 'Total energy consumption by room',
			})}
		>
			{#if failedRoomCount > 0}
				<div class="room-distribution-partial" role="status">
					<Text styleType="body-small">
						{$format('energy_monitoring_room_distribution_partial', {
							default: '{count} rooms could not be loaded.',
							values: { count: failedRoomCount },
						})}
					</Text>
					<Button variant="secondary" on:click={refresh}>
						{$format('button_retry', { default: 'Retry' })}
					</Button>
				</div>
			{/if}

			<div class="room-distribution-scroll">
				<div class="room-distribution-rows" role="list">
					{#each visibleRows as row (row.id)}
						<div class="room-distribution-row" role="listitem">
							<div class="room-distribution-name">
								<Text styleType="body-small">{row.name}</Text>
							</div>
							<div class="room-distribution-track">
								<span
									class="room-distribution-bar"
									style={`--room-distribution-width:${maxWh > 0 && row.totalWh != null ? (row.totalWh / maxWh) * 100 : 0}%`}
								></span>
								<div class="room-distribution-value">
									<span class="room-distribution-value-text">
										{row.error
											? $format(
													'energy_monitoring_room_distribution_unavailable',
													{
														default: 'Unavailable',
													},
												)
											: row.totalWh == null
												? $format(
														'energy_monitoring_room_distribution_no_data',
														{
															default: 'No data',
														},
													)
												: row.valueLabel}
									</span>
								</div>
							</div>
						</div>
					{/each}
				</div>
				<div class="room-distribution-axis" aria-hidden="true">
					<span></span>
					<div class="room-distribution-axis-values">
						{#each axisTicks as tick}
							<span style={`left:${tick.ratio * 100}%`}>{tick.label}</span>
						{/each}
					</div>
				</div>
			</div>
		</div>
	{/if}
</Container>

<style lang="scss">
	.room-distribution-loading {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 220px;
	}

	.room-distribution {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		min-width: 0;
	}

	.room-distribution-partial {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-s);
		padding: var(--spacing-xs) var(--spacing-s);
		border-radius: var(--border-radius-s);
		background: var(--color-warning-weak, rgba(255, 180, 94, 0.14));
	}

	.room-distribution-scroll {
		overflow-x: hidden;
		overflow-y: auto;
		max-height: 360px;
		padding: var(--spacing-xs) var(--spacing-xs) var(--spacing-s) 0;
		overscroll-behavior-y: contain;
		scrollbar-width: none;
	}

	.room-distribution-scroll::-webkit-scrollbar {
		display: none;
	}

	.room-distribution-rows {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
	}

	.room-distribution-row,
	.room-distribution-axis {
		display: grid;
		grid-template-columns: minmax(76px, 112px) minmax(180px, 1fr);
		align-items: center;
		gap: var(--spacing-xs);
		min-width: 0;
	}

	.room-distribution-name {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		text-align: right;
	}

	.room-distribution-track {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 30px;
		padding-right: 76px;
		border-left: 1px solid var(--color-4);
		background-image: linear-gradient(to right, var(--color-4) 1px, transparent 1px);
		background-size: 25% 100%;
	}

	.room-distribution-bar {
		display: block;
		width: var(--room-distribution-width);
		height: 18px;
		min-width: 0;
		border-radius: 0 var(--border-radius-xs) var(--border-radius-xs) 0;
		background: linear-gradient(90deg, var(--color-primary), var(--color-lightblue));
		transition: width 180ms ease;
	}

	.room-distribution-value {
		position: absolute;
		right: 0;
		min-width: 68px;
		text-align: right;
		white-space: nowrap;
	}

	.room-distribution-value-text {
		font-size: 11px;
		font-weight: 600;
	}

	.room-distribution-axis {
		margin-top: var(--spacing-xs);
	}

	.room-distribution-axis-values {
		position: relative;
		height: 20px;
		margin-right: 76px;
		border-top: 1px solid var(--color-4);
	}

	.room-distribution-axis-values span {
		position: absolute;
		top: var(--spacing-xxs);
		color: var(--color-text-secondary);
		font-size: 10px;
		transform: translateX(-50%);
		white-space: nowrap;
	}

	.room-distribution-axis-values span:first-child {
		transform: none;
	}

	.room-distribution-axis-values span:last-child {
		transform: translateX(-100%);
	}

	@media (max-width: 520px) {
		.room-distribution-row,
		.room-distribution-axis {
			grid-template-columns: 88px minmax(0, 1fr);
			gap: var(--spacing-xs);
		}

		.room-distribution-track {
			padding-right: 52px;
		}

		.room-distribution-value {
			min-width: 48px;
		}

		.room-distribution-axis-values {
			margin-right: 52px;
		}

		.room-distribution-axis-values span:nth-child(even) {
			display: none;
		}
	}
</style>
